import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { OverlayContainer, useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';

import type { FormHTMLAttributes, KeyboardEvent, PropsWithChildren, ReactNode } from 'react';
import type { AriaDialogProps } from '@react-types/dialog';

import styles from './ModalDialog.module.css';

import { Backdrop } from 'components/Backdrop';
import { OverlayCloseButton } from 'components/OverlayCloseButton';
import { Spinner } from 'components/Spinner';

export interface ModalDialogProps {
  /** The buttons of the dialog. */
  buttons?: ReactNode;
  /** `true` if the dialog can be closed by clicking outside of it; otherwise, `false`. The default is `false`. */
  isDismissable?: boolean;
  /** `true` to disable closing the dialog with the keyboard; otherwise, `false`. The default is `false`. */
  isKeyboardDismissDisabled?: boolean;
  /** `true` to show a spinner, if the contents of the dialog is lazy loaded; otherwise, `false`. The default is `false`. */
  isLoading?: boolean;
  /** The `aria-valuetext` value of the [Spinner](/story/atoms-spinner--spinner-example) component. The default is `'Loading'` Used together with `isLoading`. */
  spinnerAriaValueText?: string;
  /** The `aria-label` value of the [OverlayCloseButton](/story/atoms-overlayclosebutton--overlay-close-button-example) component. The default is `'Close'` */
  closeButtonAriaValueText?: string;
  /** `true` to render the dialog opened; otherwise, `false`. The default is `false`. */
  isOpen: boolean;
  /** `true` to disable form validation; otherwise, `false`. The default is `false`. */
  noValidate?: boolean;
  /** The callback function that is invoked when the dialog is closed. */
  onClose?(): void;
  /** The callback function that is invoked when the form is submitted. */
  onSubmit?: FormHTMLAttributes<HTMLFormElement>['onSubmit'];
  /** The `aria-role` value of the dialog. Possible values are `'dialog'`, `'alertdialog'`. The default is `'dialog'`. */
  role?: AriaDialogProps['role'];
  /** The title of the dialog. */
  title?: string;
  /** The contents of the dialog header. */
  headerContent?: ReactNode;
  /** The variant of the dialog. */
  variant?: 'confirm' | 'dialog';
  /** `true` to add padding to the dialog; otherwise, `false`. The default is `false`. */
  padded?: boolean;
  /** Size of the dialog. */
  size?: 'small' | 'regular' | 'medium' | 'large';
  /** `true` to show a border between the main content of the dialog and the buttons; otherwise, `false`. The default is `false`. */
  noButtonBorder?: boolean;
}

/** Represents a component for a modal dialog. */
export function ModalDialog(props: PropsWithChildren<ModalDialogProps>) {
  const {
    buttons,
    children,
    isDismissable = true,
    isKeyboardDismissDisabled = !isDismissable,
    isLoading,
    spinnerAriaValueText = 'Loading',
    closeButtonAriaValueText = 'Close',
    isOpen,
    noValidate = false,
    onClose,
    onSubmit,
    role = 'dialog',
    title,
    headerContent,
    variant = 'dialog',
    padded,
    size = 'regular',
    noButtonBorder = false,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { dialogProps, titleProps } = useDialog({ role }, ref);
  const { modalProps } = useModal({ isDisabled: !isOpen });
  const { overlayProps, underlayProps } = useOverlay(
    { isDismissable, isKeyboardDismissDisabled, isOpen, onClose },
    ref
  );

  usePreventScroll({ isDisabled: !isOpen });

  useEffect(() => {
    const formElement = formRef.current;

    function checkScrollable() {
      if (formElement) {
        const scrollHeight = formElement.scrollHeight;
        const clientHeight = formElement.clientHeight;
        const scrollTop = formElement.scrollTop;

        setIsScrollable(scrollHeight > clientHeight);
        setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight);
      }
    }

    function handleScrollAndResize() {
      checkScrollable();
    }

    if (formElement) {
      formElement.addEventListener('scroll', handleScrollAndResize);
      window.addEventListener('resize', handleScrollAndResize);
    }

    const timeoutId = setTimeout(() => {
      checkScrollable();
    }, 0);

    return () => {
      if (formElement) {
        formElement.removeEventListener('scroll', handleScrollAndResize);
      }
      window.removeEventListener('resize', handleScrollAndResize);
      clearTimeout(timeoutId);
    };
  }, [isOpen, headerContent]);

  function onKeyUp(event: KeyboardEvent) {
    event.stopPropagation();
  }

  if (!isOpen) return null;

  return (
    <OverlayContainer>
      <Backdrop {...underlayProps}>
        {isLoading ? (
          <Spinner ariaValueText={spinnerAriaValueText} />
        ) : (
          <>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <FocusScope autoFocus contain restoreFocus>
              <div
                {...dialogProps}
                {...modalProps}
                {...overlayProps}
                className={clsx(styles['modal-dialog'], [styles[`modal-dialog--${size}`]], {
                  [styles['modal-dialog--confirm']]: variant === 'confirm',
                  [styles['modal-dialog--padded']]: padded,
                })}
                onKeyUp={onKeyUp}
                role="presentation"
                ref={ref}
              >
                {(title || headerContent) && variant === 'dialog' && (
                  <>
                    {title && (
                      <h1 {...titleProps} className={styles['modal-dialog__title']}>
                        {title}
                      </h1>
                    )}
                    {headerContent}
                    {variant === 'dialog' && (
                      <span className={styles['modal-dialog__close']}>
                        <OverlayCloseButton
                          aria-label={closeButtonAriaValueText}
                          onClick={onClose}
                          paddedModal={padded}
                        />
                      </span>
                    )}
                  </>
                )}
                <form
                  className={clsx(styles['modal-dialog__content'], {
                    [styles['modal-dialog--content-with-buttons']]: buttons,
                  })}
                  onSubmit={onSubmit}
                  noValidate={noValidate}
                  ref={formRef}
                >
                  <div className={clsx({ [styles['modal-dialog__children']]: !noButtonBorder })}>{children}</div>
                  {buttons && (
                    <div
                      className={clsx(styles['modal-dialog__buttons'], {
                        [styles['modal-dialog__buttons--border']]: !noButtonBorder,
                        [styles['modal-dialog__buttons--shadow']]: isScrollable && !isScrolledToBottom,
                      })}
                    >
                      {buttons}
                    </div>
                  )}
                </form>
              </div>
            </FocusScope>
          </>
        )}
      </Backdrop>
    </OverlayContainer>
  );
}
